import React, { useContext, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import produce from 'immer';
import { styled } from 'linaria/react';
import { useSetRecoilState } from 'recoil';
import { Checkbox, SelectionGroup, SelectionGroupItem, Typography, Adaptive } from 'fronton-react';
import { WarningIcon } from '@fronton/icons-react';
import { SelectionGroupValue } from 'fronton-react/selection-group/types';
import { RouteContext, TExtendedSurfaceItem } from 'entries/common/widgets/context';
import { gaClickOnSurface } from 'ga-events/ga-click-surface';
import { gaClickOnMaterial } from 'ga-events/ga-click-material';
import { gaClickSurfaceStep } from 'ga-events/ga-surface-step-click';
import { SurfaceInputs } from './types';
import { useSurfaceState } from './store/hooks/surface';
import { useMaterialState } from './store/hooks/material';
import { useSurfaces } from './hooks/use-surfaces';
import { Infobar } from './components/Infobar';
import { dimensionsFormValues } from '../dimensions/store/dimensions';
import { AdaptiveTypography } from './components/Typography';
import { doorsWindowsArea } from '../dimensions/store/area';
import { roomArea } from '../common/store/rooms';
import { SurfaceFooter } from './components/SurfaceFooter';

export const SurfaceForm = () => {
  const { surfaceFormState, setSurfaceState } = useSurfaceState();
  const { materialFormState, setMaterial } = useMaterialState();
  const surfaces = useSurfaces();
  const { setContext: setRoute } = useContext(RouteContext);
  const setDimensionState = useSetRecoilState(dimensionsFormValues);
  const setRoomArea = useSetRecoilState(roomArea);
  const setDoorsWindowsArea = useSetRecoilState(doorsWindowsArea);
  const hasMaterials = surfaces.some(surface => surface.materials.length > 0);

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    clearErrors,
    watch,
  } = useForm<SurfaceInputs>({
    defaultValues: surfaceFormState,
  });

  const currentValues = watch();

  useEffect(() => {
    if (isDirty) {
      clearErrors();
    }
  }, [isDirty, clearErrors]);

  const onSurfaceChange = ({
    onChange,
    data,
  }: {
    onChange: (value: boolean) => void;
    data: TExtendedSurfaceItem;
  }) => (surfaceValue: boolean) => {
    onChange(surfaceValue);
    gaClickOnSurface({ name: data.label, checked: surfaceValue });
    if (data.materials?.length) {
      setMaterial(prevState => {
        if (!surfaceValue) {
          return produce(prevState, draft => {
            delete draft[data.name];
          });
        }

        const defaultSurfaceValue = data.materials[0].label;

        return produce(prevState, draft => {
          draft[data.name] = {
            name: data.label,
            value: defaultSurfaceValue,
          };
        });
      });
    }
  };

  const onMaterialChange = ({ data }: { data: TExtendedSurfaceItem }) => (
    event: React.MouseEvent<Element, MouseEvent>,
    materialValue: SelectionGroupValue
  ) => {
    gaClickOnMaterial({ surfaceName: data.label, materialName: materialValue });
    setMaterial(prevState => ({
      ...prevState,
      [data.name]: {
        name: data.label,
        value: materialValue,
      },
    }));
  };

  const onSubmit: SubmitHandler<SurfaceInputs> = formData => {
    gaClickSurfaceStep({ materialState: materialFormState, surfaceFormData: formData, surfaces });

    setSurfaceState(state => ({ ...state, ...formData }));
    setDimensionState({});
    setRoomArea(false);
    setDoorsWindowsArea(false);
    setRoute('dimensions');
  };

  return (
    <SurfaceFormContainer>
      <TextContainer>
        <Adaptive to="tablet">
          <AdaptiveTypography variant="m" size="body_long" text="Какие поверхности вас интересуют?" />
        </Adaptive>
        <Adaptive from="tablet">
          <AdaptiveTypography variant="l" size="body_long" text="Какие поверхности вас интересуют?" />
        </Adaptive>
        <Adaptive to="tablet">
          <AdaptiveTypography
            variant="s"
            size="body_short"
            text="Выберите одну или несколько поверхностей"
          />
        </Adaptive>
        <Adaptive from="tablet">
          <AdaptiveTypography
            variant="m"
            size="body_short"
            text="Выберите одну или несколько поверхностей"
          />
        </Adaptive>
      </TextContainer>
      <form id="surface-form" onSubmit={handleSubmit(onSubmit)}>
        {surfaces.map((surface, index) => (
          <Controller
            key={surface.name}
            name={surface.name}
            control={control}
            rules={{
              required: !Object.values(currentValues).some(value => value),
            }}
            render={({ field: { value, onChange } }) => (
              <CheckboxContainer last={surfaces.length === index + 1}>
                <Adaptive from="tablet">
                  <Checkbox
                    size="l"
                    name={`${surface.name}-mobile`}
                    role="checkbox"
                    label={surface.label}
                    checked={value}
                    onChange={onSurfaceChange({ onChange, data: surface })}
                  />
                </Adaptive>
                <Adaptive to="tablet">
                  <Checkbox
                    size="m"
                    name={`${surface.name}-desktop`}
                    role="checkbox"
                    label={surface.label}
                    checked={value}
                    onChange={onSurfaceChange({ onChange, data: surface })}
                  />
                </Adaptive>
                {value && surface.materials.length > 1 && (
                  <MaterialContainer>
                    <Typography variant="caption" color="text-minor">
                      {surface.materialLabel}
                    </Typography>
                    <SelectionGroup
                      active={materialFormState[surface.name].value}
                      onChange={onMaterialChange({ data: surface })}
                      size="s"
                      wide
                      rounded>
                      {surface.materials.map(material => (
                        <SelectionGroupItem key={material.name} value={material.name}>
                          {material.label}
                        </SelectionGroupItem>
                      ))}
                    </SelectionGroup>
                  </MaterialContainer>
                )}
              </CheckboxContainer>
            )}
          />
        ))}
        {Object.keys(errors).length !== 0 && (
          <Errors>
            <WarningIcon type="fill" size="m" color="attention-primary" />
            <Typography variant="caption" color="attention-primary">
              Выберите поверхности
            </Typography>
          </Errors>
        )}
      </form>
      {hasMaterials ? (
        <InfobarWrapper>
          <Infobar />
        </InfobarWrapper>
      ) : null}
      <FooterContainer>
        <FooterWrapper>
          <Adaptive to="tablet">
            <SurfaceFooter size="l" wide />
          </Adaptive>
          <Adaptive from="tablet">
            <SurfaceFooter size="l" />
          </Adaptive>
        </FooterWrapper>
      </FooterContainer>
    </SurfaceFormContainer>
  );
};

const SurfaceFormContainer = styled.div`
  margin-top: 16px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 32px;
`;

const Errors = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 16px;
`;

const CheckboxContainer = styled.div<{ last: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: ${props => (props.last ? '0px' : '32px')};
`;

const MaterialContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfobarWrapper = styled.div`
  margin-top: 32px;
`;

const FooterContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media (--to-tablet) {
    bottom: var(--space-25);
    flex-direction: column;
    margin-top: auto;
  }
  @media (--only-mobile) {
    position: fixed;
    bottom: var(--space-200);
    z-index: 999;
    width: calc(100% - var(--space-400));
  }
`;

export const FooterWrapper = styled.div`
  position: relative;
  margin-top: var(--space-250);
`;
